import { useContext } from 'react'
import { useAuthStore } from '../stores/authStore'
import { OrganizadorContext } from '../contexts/OrganizadorContext'

/**
 * Hook de permissão granular por módulo.
 *
 * modulos_acesso pode conter:
 *   'clientes'            → acesso completo
 *   'clientes-visualizar' → só leitura
 *   (ausente)             → sem acesso
 *
 * Módulos com suporte a visualização parcial: clientes, leiloes, lotes
 */

const ACESSO_TOTAL_ROLES = ['super_admin', 'admin', 'escritorio']

export function usePermissao() {
  const { user } = useAuthStore()
  const orgCtx = useContext(OrganizadorContext)
  const orgSomenteLeitura = !!orgCtx?.organizadorAtivo?.somente_visualizacao

  /**
   * Retorna true se o usuário pode EDITAR o módulo.
   * Roles admin/escritorio → sempre true (super_admin nunca é bloqueado).
   * Personalizado          → precisa ter o ID exato (sem -visualizar).
   * Se o organizador está em modo somente visualização → sempre false.
   */
  const podeEditar = (modulo) => {
    if (!user) return false
    const nivel = user.permission_level
    // Super admin nunca é bloqueado, mesmo em orgs em modo leitura
    if (nivel === 'super_admin') return true
    // Bloqueia edição para todos os outros quando org está em modo leitura
    if (orgSomenteLeitura) return false
    if (ACESSO_TOTAL_ROLES.includes(nivel)) return true
    if (nivel === 'manejo') return ['lotes', 'clientes', 'relatorios', 'dashboard'].includes(modulo)
    if (nivel === 'transmissao') return false
    if (nivel === 'personalizado') {
      const acesso = user.modulos_acesso || []
      // 'gestao' (legado) concede acesso total a todos os sub-módulos de gestão
      if (modulo.startsWith('gestao-') && acesso.includes('gestao')) return true
      return acesso.includes(modulo)
    }
    return false
  }

  /**
   * Retorna true se o usuário é estritamente "só visualização" no módulo
   * (tem -visualizar mas NÃO tem o ID completo).
   */
  const soVisualizacao = (modulo) => {
    if (!user) return false
    if (ACESSO_TOTAL_ROLES.includes(user.permission_level)) return false
    if (user.permission_level === 'manejo') return false
    if (user.permission_level === 'personalizado') {
      const m = user.modulos_acesso || []
      // 'gestao' legado = acesso completo, não é "só visualização"
      if (modulo.startsWith('gestao-') && m.includes('gestao')) return false
      return m.includes(`${modulo}-visualizar`) && !m.includes(modulo)
    }
    return false
  }

  /**
   * Retorna true se o usuário pode ao menos VISUALIZAR o módulo
   * (acesso completo ou só leitura).
   */
  const podeVisualizar = (modulo) => {
    return podeEditar(modulo) || soVisualizacao(modulo)
  }

  return { podeEditar, soVisualizacao, podeVisualizar }
}
